"use client";

import React, { useRef } from "react";
import { Button, Input } from "visuais";
import { Plus, X } from "lucide-react";

// =================================================================
// Tipos
// =================================================================

interface UploadAreaProps {
  onFilesSelected: (files: FileList | File[]) => void;
  selectedFiles: File[];
  fileDescriptions: Record<string, string>;
  onFileDescriptionChange: (fileName: string, description: string) => void;
  onClearFiles: () => void;
  disabled?: boolean;
}

// =================================================================
// Componente
// =================================================================

export function UploadArea({
  onFilesSelected,
  selectedFiles,
  fileDescriptions,
  onFileDescriptionChange,
  onClearFiles,
  disabled = false,
}: UploadAreaProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="space-y-4">
      {/* Área de Upload */}
      <div className="space-y-2">
        <h4 className="text-xs font-medium text-muted-foreground">
          Adicionar Anexos
        </h4>
        <div
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = "copy";
          }}
          onDrop={(e) => {
            e.preventDefault();
            if (!disabled) {
              onFilesSelected(e.dataTransfer.files);
            }
          }}
          onClick={() => !disabled && inputRef.current?.click()}
          className={`w-full h-20 rounded-lg border-2 border-dashed p-3 flex flex-col items-center justify-center transition-colors ${
            disabled
              ? "border-muted bg-muted/20 cursor-not-allowed"
              : "border-primary/30 cursor-pointer hover:bg-primary/5 hover:border-primary/50"
          }`}
        >
          <Plus
            className={`w-4 h-4 mb-1 ${
              disabled ? "text-muted-foreground" : "text-primary"
            }`}
          />
          <p
            className={`text-xs text-center ${
              disabled ? "text-muted-foreground" : "text-foreground/80"
            }`}
          >
            {disabled ? "Upload desabilitado" : "Arraste arquivos aqui"}
          </p>
          <input
            type="file"
            multiple
            hidden
            ref={inputRef}
            disabled={disabled}
            onChange={(e) => {
              if (e.target.files && !disabled) {
                onFilesSelected(e.target.files);
              }
              e.target.value = "";
            }}
          />
        </div>
      </div>

      {/* Arquivos Selecionados */}
      {selectedFiles.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-medium text-muted-foreground">
              Selecionados ({selectedFiles.length})
            </h4>
            <Button
              variant="outline"
              size="sm"
              onClick={onClearFiles}
              className="h-6 text-xs"
            >
              <X className="h-3 w-3 mr-1" />
              Limpar
            </Button>
          </div>
          <div className="space-y-1">
            {selectedFiles.map((file, index) => (
              <div
                key={index}
                className="space-y-1 p-2 bg-muted/30 rounded text-xs"
              >
                <div className="flex items-center justify-between">
                  <span className="truncate flex-1 font-medium">
                    {file.name}
                  </span>
                  <span className="text-muted-foreground ml-2">
                    {(file.size / 1024).toFixed(1)} KB
                  </span>
                </div>
                <Input
                  placeholder="Descrição do arquivo"
                  value={fileDescriptions[file.name] || ""}
                  onChange={(e) =>
                    onFileDescriptionChange(file.name, e.target.value)
                  }
                  className="h-6 text-xs"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
